// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import { xIsAuthenticated } from "@west/App/Store/Selectors/SessionSelectors";
import { XNGAGE_CUSTOM_WIDGET } from "@west/XDL/constants";
import { mergeWithCss } from "@west/XDL/Utilities";
import { xTranslate } from "@west/XDL/Utilities/translate";
import React, { useState } from "react";
import { FC } from "react";

interface OwnProps {
    extendedStyles?: AuthenticationPromptStyles;
}

export interface AuthenticationPromptStyles {
    container?: InjectableCss;
    message?: TypographyPresentationProps;
    viewLink?: LinkPresentationProps;
}

export const authenticationPromptStyles: AuthenticationPromptStyles = {};

const AuthenticationPrompt: FC<OwnProps> = ({ extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(authenticationPromptStyles, extendedStyles));

    const history = useHistory();
    const { isAuthenticated, signInUrl, currentLocation, createAccountUrl } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        createAccountUrl: getPageLinkByPageType(state, "CreateAccountPage")?.url,
        currentLocation: getLocation(state),
    }));

    if (isAuthenticated) {
        return null;
    }

    const redirectToSignInPage = () => {
        if (!signInUrl) {
            logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            }
        }
    };
    const redirectToCreateAccountPage = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="AuthenticationPrompt">
            <Typography {...styles.message}>
                {xTranslate(
                    `{0} or {1} for lower prices and free shipping.`,
                    <Link
                        onClick={redirectToSignInPage}
                        {...styles.viewLink}
                        key={`${uniqueId()}-Sign In`}
                        aria-label={translate("Sign In")}
                    >
                        {translate("Sign In")}
                    </Link>,
                    <Link
                        onClick={redirectToCreateAccountPage}
                        {...styles.viewLink}
                        key={`${uniqueId()}-Sign Up`}
                        aria-label={translate("Sign Up")}
                    >
                        {translate("Sign Up")}
                    </Link>,
                )}
            </Typography>
        </StyledWrapper>
    );
};

const AuthenticationPromptWidget: FC<OwnProps> = props => <AuthenticationPrompt {...props} />;

const widgetModule: WidgetModule = {
    component: AuthenticationPromptWidget,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
    },
};

export default widgetModule;
export { AuthenticationPrompt };
